// src/services/enrichEnumFields.ts
//
// Client-side mirror of functions/src/enrichEnumFields.ts. functions/ has
// `rootDir: "src"` in its tsconfig so it cannot import across the package
// boundary — the two copies are kept in sync BY HAND, the same arrangement as
// functions/src/calibreKey.ts ↔ src/services/calibreResolution.ts.
// Changes here MUST be mirrored there.
//
// The authoritative enum check runs server-side in the enrichReference response
// processor, which strict-drops any value that isn't in the field's lookup. This
// copy exists for defence in depth: the editor uses it to refuse to AUTO-ACCEPT
// a high-confidence suggestion it can't verify, so if the server check is ever
// bypassed (an older deployed function, a lookup that was unreadable server-side)
// the value still has to pass a human's eye before it lands on a reference.
//
// The unit tests for the shared logic live next to this file
// (enrichEnumFields.test.ts) — functions/ has no test runner, and the logic is
// identical in both copies.

/**
 * Suggestion key (as returned by the enrichReference `submit_suggestions` tool)
 * → the Firestore lookup collection whose `values[].value` slugs are the ONLY
 * legal values for that key.
 *
 * Derived from the {{LOOKUP_*}} tokens in the live enrichReference prompt: a
 * token `{{LOOKUP_DIAL_NUMERALS}}` resolves to collection `lookup_dial_numerals`
 * (renderLookupTokens lowercases the token body and prefixes `lookup_`). Kept as
 * a constant rather than re-parsing the prompt on every request.
 *
 * Note the several cases where the field name and the collection name diverge —
 * these are exactly the pairs that make cross-field bleed hard to spot by eye:
 *   dialIndexes     → lookup_dial_marker_type
 *   hands           → lookup_dial_hands
 *   strapBuckleType → lookup_clasp_type
 *   caseBack        → lookup_caseback_type
 */
export const ENRICH_FIELD_LOOKUPS: Record<string, string> = {
  // MOVEMENT
  movementType: 'lookup_movement_types',

  // CASE
  caseMaterial: 'lookup_case_materials',
  caseShape: 'lookup_case_shapes',
  crystal: 'lookup_crystal_materials',
  crownType: 'lookup_crown_types',
  caseFinish: 'lookup_case_finishes',
  // Multi-select, like bezelColors — validateEnumField filters arrays per
  // element, so a two-tone case keeps whichever colours are legal.
  caseColors: 'lookup_case_colors',
  caseBack: 'lookup_caseback_type',
  caseCoating: 'lookup_case_coatings',
  bezelType: 'lookup_bezel_types',
  // `bezelMaterial` is the suggestion key; it persists to WatchReference.bezel,
  // which has always been the bezel MATERIAL despite the shorter name.
  bezelMaterial: 'lookup_bezel_material',
  // Multi-select — validateEnumField filters arrays per element, so a Pepsi
  // bezel returning ["red", "blue", "cerachrom"] keeps the two real colors and
  // drops the material word rather than losing the whole field.
  bezelColors: 'lookup_bezel_colors',

  // ELECTRONICS / MODULE
  elecLcdType: 'lookup_lcd_type',
  elecLightType: 'lookup_light_type',
  // The display's own colours, stored per-ref at electronics.displayColor /
  // textColor / backlightColor. Backlight colour SHARES lookup_text_colors with
  // text colour — both are colours of light on the display — so a bare "white"
  // or "amber" validates against the same list for either.
  elecDisplayColor: 'lookup_display_colors',
  elecTextColor: 'lookup_text_colors',
  elecBacklightColor: 'lookup_text_colors',

  // DIAL & HANDS
  dialColor: 'lookup_dial_colors',
  dialMaterial: 'lookup_dial_materials',
  dialFinish: 'lookup_dial_finish',
  // `dialIndexes` is the MARKER TYPE — how the markers are constructed
  // (applied / printed / sandwich / engraved). It shared lookup_dial_numerals
  // with dialNumerals until the marker-type split, which is what let numeral
  // slugs land in dialAndHands.indexes. The stored prompt has pointed at
  // {{LOOKUP_DIAL_MARKER_TYPE}} for longer than this table has.
  dialIndexes: 'lookup_dial_marker_type',
  dialNumerals: 'lookup_dial_numerals',
  hands: 'lookup_dial_hands',
  handsColor: 'lookup_hand_colors',
  indexColor: 'lookup_index_colors',
  handTypes: 'lookup_hand_types',
  dialStyle: 'lookup_dial_styles',
  // WHAT each sub-register does — the companion to the plain `subdials` COUNT,
  // which is an integer and so has no lookup to validate against. MULTI-select
  // backed by lookup_dial_subdials, so validateEnumField filters the array per
  // element the way it does bezelColors and dateWheelTextColor.
  subdialTypes: 'lookup_dial_subdials',

  // CHAPTER RING & DIAL FURNITURE
  // The ring around the dial's edge, what is printed on it, the measurement
  // scales the watch carries and how the brand mark is applied. The first two
  // and the last are single-select; `dialScales` is MULTI and comma-joined, so
  // validateEnumField filters it per element the way it does subdialTypes.
  //
  // None of the four gates another — unlike the lume block below, where
  // `lumePresence: none` means there is nothing left to describe.
  chapterRing: 'lookup_chapter_ring_type',
  minuteTrack: 'lookup_minute_track_type',
  dialScales: 'lookup_dial_scales',
  // Multi-select: validateEnumField filters the array per element, so an
  // unrecognised feature is dropped without losing the recognised ones.
  dialFeatures: 'lookup_dial_features',
  logoType: 'lookup_dial_logo_type',

  // LUME
  // The detail behind the `hasLume` boolean above — where the material is, what
  // it is, and its two colours (unlit in daylight vs glowing in the dark, which
  // are genuinely different answers: a faux-patina `cream` dial glows plain
  // green). All four are single-select, all four persist flat under
  // dialAndHands, and `lumePresence` gates the other three — a `none` answer
  // means there is no material to describe.
  lumePresence: 'lookup_lume_presence',
  lumeMaterial: 'lookup_lume_material',
  lumeDayColor: 'lookup_lume_day_color',
  lumeGlowColor: 'lookup_lume_glow_color',

  // DATE
  // All read straight off a dial photo. The first four are single-select; only
  // dateWheelTextColor is multi, and validateEnumField filters its array per
  // element the way it does bezelColors.
  //
  // dateWheelTextColor deliberately SHARES lookup_text_colors with the
  // electronics `textColor` field rather than forking a wheel-specific copy: the
  // numerals printed on a date disc come in the same handful of colours a
  // digital readout does. That shared doc is flagged isMultiSelect: false for
  // textColor's sake, which is why the enrich prompt spells out "return an
  // array" for this field instead of relying on the rendered token's header.
  dateDisplay: 'lookup_date_display',
  dateWindowPosition: 'lookup_date_window_position',
  dateWindowFrame: 'lookup_date_window_frame',
  dateWheelColor: 'lookup_date_wheel_color',
  dateWheelTextColor: 'lookup_text_colors',

  // STRAP / BRACELET
  strapMaterial: 'lookup_strap_materials',
  strapColor: 'lookup_strap_color',
  strapBuckleType: 'lookup_clasp_type',

  // CLASSIFICATION
  watchType: 'lookup_watch_types',
  functions: 'lookup_watch_functions',
  occasion: 'lookup_watch_occasion',

  // POCKET WATCH
  caseStyle: 'lookup_pocket_watch_case_style',
  escapement: 'lookup_pocket_watch_escapement',
  jewelBearing: 'lookup_pocket_watch_jewel_bearing',
  movementSize: 'lookup_pocket_watch_movement_size',
  adjustments: 'lookup_pocket_watch_adjustments',
};

/** The subset of a lookup value this module needs. Structurally compatible with
 *  both the functions-side LookupValueLite and the client-side LookupValue. */
export interface EnumCandidate {
  value?: string;
  synonyms?: string[];
  isActive?: boolean;
  excludeFromAI?: boolean;
}

export type EnumMatchStatus = 'exact' | 'synonym' | 'unknown';

export interface EnumMatch {
  status: EnumMatchStatus;
  /** The canonical slug, present when status is 'exact' or 'synonym'. */
  resolved?: string;
}

function norm(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase()
    : String(value ?? '').trim().toLowerCase();
}

/**
 * The candidates the AI was actually offered.
 *
 * This predicate MUST stay identical to the filter in renderLookupTokens
 * (functions/src/aiEnrich.ts) — it is what makes the validator neither stricter
 * nor looser than the prompt. A value hidden from the AI (`excludeFromAI`) or
 * retired (`isActive: false`) was never in the bullet list, so returning it is
 * drift and must be dropped, exactly like an invented slug.
 */
function offeredCandidates(values: EnumCandidate[]): EnumCandidate[] {
  return values.filter(
    (v) => v.excludeFromAI !== true && v.isActive !== false && !!v.value,
  );
}

/**
 * Resolve one AI-returned value against a lookup's current value list.
 *
 * Matching order:
 *   1. exact   — case-insensitive, trimmed match on `values[].value`
 *   2. synonym — case-insensitive, trimmed match on any `values[].synonyms[]`,
 *                canonicalized to that entry's `value`. The prompt advertises
 *                synonyms in the `(also: …)` parenthetical, so a model returning
 *                one is following instructions, not drifting.
 *   3. unknown — caller strict-drops.
 */
export function canonicalizeEnumValue(
  raw: unknown,
  values: EnumCandidate[],
): EnumMatch {
  const needle = norm(raw);
  if (!needle) return { status: 'unknown' };

  const candidates = offeredCandidates(values);

  for (const c of candidates) {
    if (norm(c.value) === needle) {
      return { status: 'exact', resolved: c.value as string };
    }
  }

  for (const c of candidates) {
    const synonyms = Array.isArray(c.synonyms) ? c.synonyms : [];
    for (const s of synonyms) {
      if (norm(s) === needle) {
        return { status: 'synonym', resolved: c.value as string };
      }
    }
  }

  return { status: 'unknown' };
}

/** The slugs currently on offer, for drift log lines / debugging. */
export function offeredSlugs(values: EnumCandidate[]): string[] {
  return offeredCandidates(values).map((v) => v.value as string);
}

export interface EnumFieldResult {
  /** The value to keep, or undefined when the whole field is strict-dropped. */
  value?: string | string[];
  /** Values that matched nothing and were dropped (for drift logging). */
  dropped: string[];
  /** Values rewritten from a synonym to their canonical slug, as `from → to`. */
  canonicalized: Array<{ from: string; to: string }>;
}

/**
 * Validate one suggestion value (scalar or array) against a lookup list.
 *
 * Arrays are filtered PER ELEMENT — a multi-select field that returns three
 * good slugs and one invented one keeps the three. The field is dropped
 * entirely only when nothing survives.
 */
export function validateEnumField(
  raw: unknown,
  values: EnumCandidate[],
): EnumFieldResult {
  const dropped: string[] = [];
  const canonicalized: Array<{ from: string; to: string }> = [];

  if (Array.isArray(raw)) {
    const kept: string[] = [];
    for (const element of raw) {
      const match = canonicalizeEnumValue(element, values);
      if (match.status === 'unknown' || !match.resolved) {
        dropped.push(String(element));
        continue;
      }
      if (match.status === 'synonym') {
        canonicalized.push({ from: String(element), to: match.resolved });
      }
      // Guard against a model listing the same concept twice (e.g. a slug and
      // one of its own synonyms) collapsing into a duplicate after canonicalization.
      if (!kept.includes(match.resolved)) kept.push(match.resolved);
    }
    return {
      value: kept.length > 0 ? kept : undefined,
      dropped,
      canonicalized,
    };
  }

  const match = canonicalizeEnumValue(raw, values);
  if (match.status === 'unknown' || !match.resolved) {
    return { value: undefined, dropped: [String(raw)], canonicalized };
  }
  if (match.status === 'synonym') {
    canonicalized.push({ from: String(raw), to: match.resolved });
  }
  return { value: match.resolved, dropped, canonicalized };
}
